'use client';

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { SUB_DOMAIN } from '@/constants';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import {
  PROMOTION_CREATIVE_SLOT,
  trackSelectPromotion,
} from '@/utils/analytics/gtm';

interface BannerItem {
  fileUrl?: string;
  appFileUrl?: string;
  linkUrl?: string;
  appLinkUrl?: string;
  targetNewYN?: boolean;
  title?: string;
}

interface MainCarouselClientProps {
  items: BannerItem[];
}

export const MainCarouselClient = ({ items }: MainCarouselClientProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isApp = useDeviceInfoStore(
    (state) => state.subDomain !== SUB_DOMAIN.WELLO,
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: items.length > 1,
      align: 'start',
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden px-10" ref={emblaRef}>
        <div className="flex">
          {items.map(
            (
              {
                fileUrl: file_url,
                appFileUrl: app_file_url,
                linkUrl: link_url,
                appLinkUrl: app_link_url,
                targetNewYN: target_new_yn,
                title,
              },
              index,
            ) => {
              const applyFileUrl = isApp ? app_file_url : file_url;
              const applyLinkUrl = isApp ? app_link_url : link_url;

              if (!(applyLinkUrl && applyFileUrl && title)) return null;

              return (
                <div key={index} className="min-w-0 flex-[0_0_100%] px-10">
                  <CustomLink
                    aria-label={title}
                    href={applyLinkUrl}
                    target={target_new_yn ? '_blank' : '_self'}
                    onClick={() => {
                      trackSelectPromotion({
                        promotion_id: applyLinkUrl,
                        promotion_name: title,
                        creative_slot:
                          PROMOTION_CREATIVE_SLOT.WELLO_HOME_MAIN_BANNER,
                      });
                    }}
                  >
                    <div className="relative aspect-[335/120] w-full overflow-hidden rounded-large">
                      <Image
                        src={applyFileUrl}
                        alt={title}
                        fill
                        sizes="calc(100vw - 20px)"
                        priority={index === 0}
                        className="object-cover"
                      />
                    </div>
                  </CustomLink>
                </div>
              );
            },
          )}
        </div>
      </div>
      <span className="absolute bottom-16 right-36 rounded-pill bg-Gray-black/20 px-8 py-2 font-500 text-Gray-white typo-caption2">
        {selectedIndex + 1} / {items.length}
      </span>
    </div>
  );
};
